import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ShoppingCardService } from '../../services/shopping-card.service';
import { CheckoutService } from '../../services/checkout.service';
import { CartService } from '../../services/cart.service';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-payment-callback',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ProgressSpinnerModule,
    ButtonModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './payment-callback.component.html',
  styleUrl: './payment-callback.component.css'
})
export class PaymentCallbackComponent implements OnInit {
  isProcessing = signal(true);
  isSuccess = signal(false);
  isError = signal(false);
  transactionRef = signal<string>('');
  errorMessage = signal<string>('');
  platformId = signal<string>('');
  platformName = signal<string>('');
  isCheckout = signal<boolean>(false); // Track if this is a checkout or card funding

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shoppingCardService: ShoppingCardService,
    private checkoutService: CheckoutService,
    private cartService: CartService,
    private authService: AuthService,
    private settingsService: SettingsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Get platform ID and name from route params
    this.route.params.subscribe(params => {
      const id = params['id'];
      let name = params['name'] || 'Store';
      
      if (name && name !== 'Store') {
        try {
          name = decodeURIComponent(name);
        } catch (e) {
          console.error('Error decoding platform name:', e);
        }
      }
      
      this.platformId.set(id);
      this.platformName.set(name);
      
      // Process payment callback after getting params
      if (this.authService.isAuthenticated()) {
        this.processPaymentCallback();
      } else {
        this.isError.set(true);
        this.errorMessage.set('You must be logged in to verify payment');
        this.isProcessing.set(false);
      }
    });
  }

  private processPaymentCallback(): void {
    // Get transaction reference from query parameters
    // Paystack: trxref, reference | Monnify: transactionReference, paymentReference | Flutterwave: tx_ref, transaction_id
    const q = this.route.snapshot.queryParams;
    const trxref = q['trxref'];
    const reference = q['reference'];
    const transactionReference = q['transactionReference'];
    const paymentReference = q['paymentReference'];
    const txRef = q['tx_ref'];
    const transactionId = q['transaction_id'];

    console.log('Payment callback received:', { trxref, reference, transactionReference, paymentReference, tx_ref: txRef, transaction_id: transactionId });

    // Prefer our reference, then gateway-specific params (Flutterwave uses tx_ref)
    const transactionRef = reference || trxref || transactionReference || paymentReference || txRef || transactionId;

    if (transactionRef) {
      this.transactionRef.set(transactionRef);
      
      // Try to verify as checkout first (since checkout is more common)
      // If that fails with "Order not found", try card funding
      this.checkoutService.verifyCheckout(transactionRef).subscribe({
        next: (response) => {
          this.isProcessing.set(false);
          
          if (response.success && response.data?.isSuccessful) {
            this.isSuccess.set(true);
            this.isCheckout.set(true); // Mark as checkout
            this.messageService.add({
              severity: 'success',
              summary: 'Payment Successful',
              detail: 'Your order has been confirmed successfully!',
              life: 5000
            });
            const platformId = this.platformId();
            if (platformId) {
              this.cartService.clearCart(platformId).subscribe({ next: () => {}, error: () => {} });
              const environment = this.settingsService.getEnvironment();
              this.shoppingCardService.clearCache(platformId, environment);
            }
          } else {
            // If checkout verification failed, try card funding
            this.tryCardFundingVerification(transactionRef);
          }
        },
        error: (error) => {
          // If error is "Order not found" or 404, try card funding verification
          if (error.status === 404 || error.error?.message?.toLowerCase().includes('order not found')) {
            console.log('Order not found, trying card funding verification...');
            this.tryCardFundingVerification(transactionRef);
          } else {
            this.isProcessing.set(false);
            this.isError.set(true);
            this.errorMessage.set(error.error?.message || 'Error verifying transaction. Please try again.');
            console.error('Transaction verification error:', error);
          }
        }
      });
    } else {
      // No transaction reference found
      this.isProcessing.set(false);
      this.isError.set(true);
      this.errorMessage.set('Invalid payment callback. No transaction reference found.');
    }
  }

  private tryCardFundingVerification(transactionRef: string): void {
    this.shoppingCardService.verifyCardFunding(transactionRef).subscribe({
      next: (response) => {
        this.isProcessing.set(false);
        
        if (response.success && response.data?.isSuccessful) {
          this.isSuccess.set(true);
          this.isCheckout.set(false); // Mark as card funding
          this.messageService.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Your card has been funded successfully!',
            life: 5000
          });
          
          // Clear shopping card cache to refresh balance
          const platformId = this.platformId();
          const environment = this.settingsService.getEnvironment();
          if (platformId) {
            this.shoppingCardService.clearCache(platformId, environment);
          }
        } else {
          this.isError.set(true);
          this.errorMessage.set(response.data?.message || response.message || 'Transaction verification failed.');
        }
      },
      error: (error) => {
        this.isProcessing.set(false);
        this.isError.set(true);
        this.errorMessage.set(error.error?.message || 'Error verifying transaction. Please try again.');
        console.error('Card funding verification error:', error);
      }
    });
  }

  continueShopping(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/store', platformId, encodedName], { replaceUrl: true });
    } else {
      this.router.navigate(['/'], { replaceUrl: true });
    }
  }

  getFormattedErrorMessage(): string {
    const error = this.errorMessage();
    
    // If error message contains "transient failure" or similar, provide a more user-friendly message
    if (error.toLowerCase().includes('transient') || error.toLowerCase().includes('exception')) {
      return 'We encountered a temporary issue while processing your payment. This could be due to a network interruption or service unavailability. Please try again in a few moments, or contact support if the problem persists.';
    }
    
    // If error message is generic or empty, provide a helpful default message
    if (!error || error.trim().length === 0) {
      return 'Unfortunately, your payment could not be processed at this time. Please check your payment details and try again, or use a different payment method.';
    }
    
    // Return the original error message but make it more readable
    return error.charAt(0).toUpperCase() + error.slice(1) + '. Please try again or contact support if the issue continues.';
  }
}
